import { C8Node, cnl } from "csi/typings";
import { makeObservable, override } from "mobx";
import { Cache, rootCache } from "./Cache";

/**
 * WorkingCache is a cache that references a secondary cache, and should be used when the
 * data being cached is temporary. For example, if you are issuing a request to the server
 * for a single view that appears infrequently and do not want to hold these nodes in memory
 * forever, then you could use this cache. It still has access to nodes in the primary cache,
 * such as user, role, and session information.
 */
class WorkingCache extends Cache {
	/**
	 * secondary cache used to augment nodes available in this cache;
	 * defaults to the rootCache storing global model instances and user information
	 */
	private readonly secondary: Cache;

	/**
	 * Return array of all nodes in both caches
	 */
	get data(): C8Node[] {
		return this.secondary.data.concat(super.data);
	}

	/**
	 * Return node with given url from this cache or secondary cache
	 */
	get(url: cnl, timestamp?: number): C8Node | null {
		const { secondary } = this;

		// check both working and secondary caches; it is possible
		// that secondary cache has newer version
		let workingNode = super.get(url, timestamp);
		const secondaryNode = secondary.get(url, timestamp);

		if (secondaryNode) {
			const workingTime = workingNode?.ModifiedAt || 0;
			const secondaryTime = secondaryNode?.ModifiedAt || 0;

			// don't need node in workingCache if also in secondary cache
			super.remove(url);

			if (secondaryTime < workingTime) {
				// update secondary cache if WorkingCache node is newer
				secondary.add(workingNode, timestamp);
			} else {
				workingNode = secondaryNode;
			}
		}

		return workingNode;
	}

	/**
	 * Add node to cache
	 */
	@override add(node: C8Node, timestamp = new Date().getTime()): C8Node {
		const { secondary } = this;
		const url = node.$URL;
		const secondaryNode = secondary.get(url);

		if (secondaryNode) {
			// if secondary cache already has node, then update there and remove from here
			secondary.add(node, timestamp);
			super.remove(url);
		} else {
			// if not in secondary cache, then just add to this cache
			super.add(node, timestamp);
		}

		return node;
	}

	/**
	 * Delete node from secondary cache as well as this cache such as when node is deleted from database.
	 * Use "remove" if you only want to remove from the WorkingCache.
	 */
	@override deleteNode(url: cnl): void {
		this.secondary.deleteNode(url);
		super.deleteNode(url);
	}

	constructor(secondary?: Cache) {
		super();
		makeObservable(this);
		this.secondary = secondary || rootCache;
	}
}

export default WorkingCache;
